import { chain } from 'lodash';
import { DateTime } from 'luxon';
import { TechnicalPointCategory } from '../../../dashboard/fleet/common/enums/technical-point-category';
import type { SwapPoint } from '../../../dashboard/fleet/common/interfaces/technical-point';
import { DeadlineHelper } from '../../helpers/deadline.helper';
import { RouteHelper } from '../../helpers/route.helper';
import { RoutePointValueHelper } from '../../helpers/route-point-value.helper';
import type {
  TelematicsRoutePoint,
  TelematicsTechnicalRoutePoint,
} from '../../interfaces/telematics/telematics-route-point';
import type { TelematicsSwap } from '../../interfaces/telematics/telematics-swap';

export class TelematicsSwapFactory {
  public static from(data: {
    routePoints: TelematicsRoutePoint[];
    fleetUuid: string;
    now: DateTime;
  }): TelematicsSwap[] {
    return data.routePoints
      .filter(
        (routePoint): routePoint is TelematicsTechnicalRoutePoint =>
          routePoint.technicalPoint?.category ===
          TechnicalPointCategory.SWAP_POINT,
      )
      .map((routePoint) => {
        const technicalPoint = routePoint.technicalPoint as SwapPoint;
        const estimations =
          RoutePointValueHelper.getEstimations(technicalPoint);
        const deadline = DeadlineHelper.getPointDeadline(technicalPoint);

        const finishedStart = chain(technicalPoint?.timestamps.arrivedAt)
          .thru((timestamp) => (timestamp ? DateTime.fromISO(timestamp) : null))
          .value();
        const estimatedStart = estimations?.arrivalTime
          ? DateTime.fromISO(estimations.arrivalTime)
          : null;
        const deadlineStart = deadline?.arrival;
        const { start: nowStart, end: nowEnd } = this.getFallbackTimes(
          technicalPoint,
          data.now,
        );

        const start =
          finishedStart ?? estimatedStart ?? deadlineStart ?? nowStart;

        const finishedEnd = chain(technicalPoint?.timestamps.completedAt)
          .thru((timestamp) => (timestamp ? DateTime.fromISO(timestamp) : null))
          .value();
        const estimatedEnd = estimations?.departureTime
          ? DateTime.fromISO(estimations.departureTime)
          : null;
        const serviceEnd = chain(technicalPoint?.timestamps.arrivedAt)
          .thru((timestamp) => (timestamp ? DateTime.fromISO(timestamp) : null))
          .thru((arrivedAt) =>
            arrivedAt?.plus({ seconds: technicalPoint.serviceTime ?? 0 }),
          )
          .value();

        const end =
          finishedEnd ??
          DateTime.max(estimatedEnd ?? serviceEnd ?? nowEnd, data.now);

        const compliance = RouteHelper.getScheduleCompliance(
          technicalPoint,
          data.routePoints,
        );

        return {
          start,
          end,
          uuid: technicalPoint.swapData.uuid,
          fleetUuid: data.fleetUuid,
          point: technicalPoint,
          compliance,
        };
      });
  }

  public static getFallbackTimes(
    point: SwapPoint,
    now: DateTime,
  ): { start: DateTime; end: DateTime } {
    const estimations = RoutePointValueHelper.getEstimations(point);
    const { summary } = point.route;
    const start = now.plus({
      seconds:
        ((estimations?.remainingDistance ?? summary.distance) /
          summary.distance || 1) * summary.duration,
    });

    return {
      start,
      end: start.plus({ seconds: point.serviceTime ?? 0 }),
    };
  }
}
